/**
 * ClinicEditBody — тело правки пакетов клиники в мобильном шите: карточки
 * пакетов (название/до N/цена, удаление), «+ пакет», ступень «Сеть» и строгая
 * валидация возрастания. Черновик локальный.
 */

import {useState} from 'react';
import {Check, CircleAlert, Plus, Trash2} from 'lucide-react';

import type {ClinicPackage} from '@services/clients';
import type {ClinicTariff, SaveClinicInput} from '@services/tariffs';
import {formatNumber} from '@utils/format';

import {newDraft, type PackageDraft, toDrafts, validatePackages} from '../../Tariffs.utils';
import {SheetFooter} from '../SheetFooter';

type ClinicEditBodyProps = {
  clinic: ClinicTariff;
  onClose: () => void;
  onAskSave: (input: SaveClinicInput) => void;
};

export const ClinicEditBody = ({clinic, onClose, onAskSave}: ClinicEditBodyProps) => {
  const [drafts, setDrafts] = useState<PackageDraft[]>(() => toDrafts(clinic.packages));
  const [overPrice, setOverPrice] = useState(String(clinic.overPrice));

  const patch = (id: string, part: Partial<PackageDraft>) =>
    setDrafts((ds) => ds.map((d) => (d.id === id ? {...d, ...part} : d)));
  const remove = (id: string) =>
    setDrafts((ds) => (ds.length <= 1 ? ds : ds.filter((d) => d.id !== id)));
  const add = () => setDrafts((ds) => [...ds, newDraft(ds[ds.length - 1])]);

  const valid = validatePackages(drafts);
  const packages: ClinicPackage[] = drafts.map(({name, upTo, price}) => ({name, upTo, price}));
  const over = Number(overPrice || 0);

  return (
    <>
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-[18px] pb-5 pt-4">
        {drafts.map((d) => (
          <div key={d.id} className="rounded-[13px] border border-skr-line bg-card p-[13px_14px]">
            <div className="flex items-center justify-between gap-2.5">
              <input
                value={d.name}
                onChange={(e) => patch(d.id, {name: e.target.value})}
                className="h-[42px] flex-1 rounded-[10px] border border-input px-3 text-[15px] font-semibold outline-none focus:border-primary"
              />
              <button
                onClick={() => remove(d.id)}
                disabled={drafts.length <= 1}
                aria-label="Удалить пакет"
                className="grid size-10 shrink-0 place-items-center text-skr-danger disabled:text-[#D2D5DB]"
              >
                <Trash2 className="size-[18px]" strokeWidth={1.9} />
              </button>
            </div>
            <div className="mt-2.5 flex gap-2.5">
              <label className="flex-1">
                <span className="text-[11.5px] text-muted-foreground">До скольких врачей</span>
                <input
                  value={String(d.upTo)}
                  onChange={(e) =>
                    patch(d.id, {upTo: Number(e.target.value.replace(/\D/g, '') || 0)})
                  }
                  inputMode="numeric"
                  className="mt-1 h-[42px] w-full rounded-[10px] border border-input px-3 font-mono text-[15px] outline-none focus:border-primary"
                />
              </label>
              <label className="flex-1">
                <span className="text-[11.5px] text-muted-foreground">Цена, сом</span>
                <input
                  value={formatNumber(d.price)}
                  onChange={(e) =>
                    patch(d.id, {price: Number(e.target.value.replace(/\D/g, '') || 0)})
                  }
                  inputMode="numeric"
                  className="mt-1 h-[42px] w-full rounded-[10px] border border-input px-3 font-mono text-[15px] outline-none focus:border-primary"
                />
              </label>
            </div>
          </div>
        ))}

        <button
          onClick={add}
          className="flex h-11 items-center justify-center gap-[7px] rounded-xl border border-dashed border-[#C9D0DA] bg-card text-[13.5px] font-medium text-primary"
        >
          <Plus className="size-4" strokeWidth={2.2} />
          Добавить пакет
        </button>

        <div className="rounded-[13px] border border-skr-line bg-card p-[13px_14px]">
          <label className="text-[11.5px] text-muted-foreground">
            Сеть · 11+ — за каждое место сверх 10, сом
          </label>
          <input
            value={formatNumber(over)}
            onChange={(e) => setOverPrice(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            className="mt-1.5 h-[42px] w-[120px] rounded-[10px] border border-input px-3 font-mono text-[15px] outline-none focus:border-primary"
          />
        </div>

        <div
          className={`flex items-center gap-2 p-0.5 text-xs ${
            valid.ok ? 'text-skr-paid' : 'text-skr-danger'
          }`}
        >
          {valid.ok ? (
            <Check className="size-3.5" strokeWidth={2.4} />
          ) : (
            <CircleAlert className="size-3.5" strokeWidth={2} />
          )}
          {valid.message}
        </div>
      </div>

      <SheetFooter
        disabled={!valid.ok}
        onClose={onClose}
        onSave={() => onAskSave({packages, overPrice: over})}
      />
    </>
  );
};
